import { sbEditable } from '@storyblok/storyblok-editable';
import gsap from 'gsap';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import styles from './SliderContent.module.scss';
import type { SliderContentProps, SliderContentRef } from './SliderContent.data';

import Heading from '../../atoms/heading/Heading';
import Image from '../../atoms/image/Image';
import useWindowSize from '../../../hooks/useWindowSize';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import { getImageSize } from 'util/storyblok/image.utils';
import { useScrollCarousel } from '../../../hooks/useScrollCarousel';
import { cleanMediaQueries } from '../../../hooks/useDeviceStateTracker';
import Text from '../../atoms/text/Text';

export default function SliderContent({
  blok,
  ref,
  ...props
}: SliderContentProps & { ref?: Ref<SliderContentRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const carouselContainerRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();
  const gridGutterRef = useRef<HTMLDivElement>(null);
  const carouselItemsRef = useRef<Array<HTMLElement>>([]);
  const imagesRef = useRef<Array<HTMLElement>>([]);

  useEffect(() => {
    if (headRef?.current) {
      const headHeight = headRef.current.offsetHeight;
      gsap.set(carouselContainerRef.current, { paddingBottom: `${headHeight}px` });
    }
  }, [windowSize, headRef, carouselContainerRef]);

  useThemeScrollBlock(elementRef, blok.use_accent_color);
  useScrollCarousel(
    {
      carouselRef: carouselContainerRef,
      marginsRef: gridGutterRef,
      carouselItemsRef,
      imagesRef,
      imageScale: blok.no_inner_parallax ? 1 : undefined,
    },
    {
      trigger: elementRef,
      pin: elementRef,
    },
  );

  return (
    <div
      ref={elementRef}
      className={classNames(styles.sliderContent, props.className, {
        [styles.withTitle]: !!blok.title,
      })}
      {...sbEditable(blok)}
    >
      <div className={styles.gridGutterWidth} ref={gridGutterRef} />

      {blok.title && (
        <div ref={headRef} className={styles.head}>
          <Heading
            as="h4"
            size="quote"
            className={classNames(styles.title, 'grid-gutter')}
            transitionInType="slideUpFadeIn"
            disableTriggerInOnScroll={false}
          >
            {blok.title}
          </Heading>
        </div>
      )}

      <div ref={carouselContainerRef} className={classNames(styles.carouselWrapper, 'grid-gutter')}>
        {blok?.assets?.map((asset, index) => (
          <div
            ref={(ref) => {
              carouselItemsRef.current[index] = ref as HTMLElement;
            }}
            key={asset.id}
            className={classNames(styles.itemHolder)}
          >
            <canvas
              width={getImageSize(asset.filename).width}
              height={getImageSize(asset.filename).height}
            ></canvas>
            <Image
              ref={(ref) => {
                imagesRef.current[index] = ref as HTMLElement;
              }}
              layout="fill"
              src={asset.filename}
              srcDesktop={asset.filename}
              sizes={`${cleanMediaQueries.MIN_768} 768px, ${cleanMediaQueries.MIN_1920} 1024px, 100vw`}
              className={classNames(styles.item)}
              alt={asset.alt}
            />
            {asset.name && carouselItemsRef.current[index] && (
              <Text
                as="figcaption"
                size="body-standard"
                disableTriggerInOnScroll={false}
                scrollTransitionVars={{ start: 'top bottom' }}
                className={styles.caption}
                style={{ width: carouselItemsRef.current[index].clientWidth }}
              >
                {asset.name}
              </Text>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
